import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { Badge, Button } from "flowbite-react";
import {
  MdGroups,
  MdTrendingUp,
  MdOutlineWarningAmber,
  MdOutlineBlock,
  MdBedtime,
  MdArrowForward,
} from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";

import { DASHBOARDS } from "../config/apiConfig";
import axios from "../config/axiosInstance";
import { useApplication } from "../contexts/ApplicationContext";
import {
  EngagementChart,
  PlanMixChart,
  RenewalChart,
  RevenueChart,
  UsageTrendChart,
} from "./customers/portfolioCharts";
import {
  buildWhatsappLink,
  describeExpiration,
  describeLastActivity,
  formatCompactMoney,
  formatDate,
  formatNumber,
  getPlanMeta,
  getStatusMeta,
} from "./customers/customerHelpers";

const KpiCard = ({ icon: Icon, label, value, hint, accent, iconClass }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-start gap-3">
      <span className={`rounded-xl p-2 ${iconClass}`}>
        <Icon size={20} />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
          {label}
        </p>
        <p className={`text-2xl font-bold leading-tight ${accent}`}>{value}</p>
        {hint ? (
          <p className="truncate text-xs text-slate-500">{hint}</p>
        ) : null}
      </div>
    </div>
  </div>
);

/**
 * Cada linha é acionável: leva direto para a tela de clientes já filtrada,
 * ou abre o WhatsApp do responsável pela empresa.
 */
const CompanyRow = ({ company, metric, onOpen }) => {
  const statusMeta = getStatusMeta(company.status);
  const planMeta = getPlanMeta(company.planId);
  const whatsappLink = buildWhatsappLink(company.phoneNumber);

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
      <button
        type="button"
        onClick={() => onOpen(company)}
        className="min-w-0 flex-1 text-left"
      >
        <p className="truncate text-sm font-medium text-slate-900 hover:text-pink-600">
          {company.name}
        </p>
        <p className="truncate text-xs text-slate-500">
          #{company.id} · {planMeta.label} · {metric(company)}
        </p>
      </button>

      <div className="flex shrink-0 items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${statusMeta.dot}`} />
        {whatsappLink ? (
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-600 transition hover:text-emerald-700"
            title={`Chamar ${company.phoneNumber}`}
          >
            <FaWhatsapp size={15} />
          </a>
        ) : null}
      </div>
    </div>
  );
};

const ListCard = ({ title, hint, count, badgeColor, items, metric, emptyLabel, onOpen, onSeeAll }) => (
  <section className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="mb-3 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          <Badge color={badgeColor}>{formatNumber(count)}</Badge>
        </div>
        {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
      </div>
      {onSeeAll && count > items.length ? (
        <Button size="xs" color="light" onClick={onSeeAll}>
          Ver todos
          <MdArrowForward className="ml-1 h-3 w-3" />
        </Button>
      ) : null}
    </div>

    <div className="flex-1 space-y-2">
      {items.length ? (
        items.map((company) => (
          <CompanyRow
            key={company.id}
            company={company}
            metric={metric}
            onOpen={onOpen}
          />
        ))
      ) : (
        <p className="py-6 text-center text-sm text-slate-500">{emptyLabel}</p>
      )}
    </div>
  </section>
);

const SkeletonCard = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="h-3 w-24 animate-pulse rounded-full bg-slate-200" />
    <div className="mt-3 h-7 w-16 animate-pulse rounded-lg bg-slate-200" />
    <div className="mt-2 h-3 w-28 animate-pulse rounded-full bg-slate-100" />
  </div>
);

/**
 * Painel do gerente de contas: o que ele tem na carteira, quem está usando e
 * o que precisa de atenção agora.
 */
const PortfolioDashboard = () => {
  const navigate = useNavigate();
  const { user } = useApplication();
  const [activeTab, setActiveTab] = useState("attention");

  const { isLoading, data } = useQuery(["dashboard-portfolio"], async () => {
    const { data: response } = await axios.get(DASHBOARDS.PORTFOLIO);

    return response.data;
  });

  const summary = data?.summary || {};
  const counts = data?.counts || {};
  const lists = data?.lists || {};
  const usageSeries = data?.usageSeries || [];
  const renewalBuckets = data?.renewalBuckets || [];
  const horizon = data?.expiringHorizonDays ?? 15;

  // A tela de clientes lê os filtros do próprio estado, então o atalho leva
  // para lá e a pessoa refina de dentro.
  const goToCustomers = () => navigate("/customers");

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
              Minha carteira
            </p>
            <h2 className="text-xl font-semibold text-slate-900">
              Olá, {user?.name?.split(" ")[0] || "tudo bem"}
            </h2>
            <p className="text-sm text-slate-500">
              Como estão os seus clientes hoje e o que precisa de atenção.
            </p>
          </div>
          <Button size="sm" color="light" onClick={goToCustomers}>
            Ver meus clientes
            <MdArrowForward className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {isLoading ? (
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </section>
      ) : (
        <>
          <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <KpiCard
              icon={MdGroups}
              label="Meus clientes"
              value={formatNumber(summary.total)}
              hint={`${formatNumber(summary.newThisMonth)} novos neste mês`}
              accent="text-slate-900"
              iconClass="bg-slate-100 text-slate-600"
            />
            <KpiCard
              icon={MdTrendingUp}
              label="Usando o sistema"
              value={formatNumber(counts.usingNow)}
              hint={`${summary.engagementRate ?? 0}% da carteira em 30 dias`}
              accent="text-emerald-600"
              iconClass="bg-emerald-50 text-emerald-600"
            />
            <KpiCard
              icon={MdOutlineWarningAmber}
              label={`Expirando (${horizon}d)`}
              value={formatNumber(counts.expiringSoon)}
              hint="Renovação a combinar"
              accent="text-amber-600"
              iconClass="bg-amber-50 text-amber-600"
            />
            <KpiCard
              icon={MdOutlineBlock}
              label="Expirados"
              value={formatNumber(counts.expired)}
              hint={`${formatCompactMoney(summary.churnRisk)} em risco/mês`}
              accent="text-rose-600"
              iconClass="bg-rose-50 text-rose-600"
            />
            <KpiCard
              icon={MdBedtime}
              label="Parados"
              value={formatNumber(counts.atRisk + counts.neverUsed)}
              hint={`${formatNumber(counts.neverUsed)} nunca usaram`}
              accent="text-violet-600"
              iconClass="bg-violet-50 text-violet-600"
            />
          </section>

          <section className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <UsageTrendChart series={usageSeries} />
            <RevenueChart series={usageSeries} />
          </section>

          <section className="grid grid-cols-1 gap-3 xl:grid-cols-3">
            <PlanMixChart plans={summary.plans} total={summary.total} />
            <EngagementChart
              health={summary.health}
              total={summary.total}
              rate={summary.engagementRate}
            />
            <RenewalChart buckets={renewalBuckets} />
          </section>

          <div className="flex flex-wrap gap-2">
            {[
              { key: "attention", label: "Precisa de atenção" },
              { key: "usage", label: "Uso" },
              { key: "recent", label: "Recentes" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium transition ${
                  activeTab === tab.key
                    ? "border-pink-500 bg-pink-50 text-pink-600"
                    : "border-slate-200 bg-white text-slate-500 hover:text-slate-800"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "attention" ? (
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
              <ListCard
                title={`Expira em até ${horizon} dias`}
                hint="Fale antes de vencer"
                count={counts.expiringSoon}
                badgeColor="warning"
                items={lists.expiringSoon || []}
                metric={(company) => describeExpiration(company.daysToExpire)}
                emptyLabel="Nenhuma renovação próxima."
                onOpen={goToCustomers}
                onSeeAll={goToCustomers}
              />
              <ListCard
                title="Expirados"
                hint="Do mais antigo para o mais recente"
                count={counts.expired}
                badgeColor="failure"
                items={lists.expired || []}
                metric={(company) => describeExpiration(company.daysToExpire)}
                emptyLabel="Nenhum cliente expirado."
                onOpen={goToCustomers}
                onSeeAll={goToCustomers}
              />
              <ListCard
                title="Pararam de usar"
                hint="Já usaram, mas sumiram há mais de 30 dias"
                count={counts.atRisk}
                badgeColor="warning"
                items={lists.atRisk || []}
                metric={(company) =>
                  `última atividade ${describeLastActivity(
                    company.lastActivityAt,
                    company.daysSinceLastActivity
                  ).toLowerCase()}`
                }
                emptyLabel="Ninguém parado na carteira."
                onOpen={goToCustomers}
                onSeeAll={goToCustomers}
              />
            </div>
          ) : null}

          {activeTab === "usage" ? (
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
              <ListCard
                title="Mais ativos (30 dias)"
                hint="Quem mais registra agendamentos"
                count={lists.topUsage?.length || 0}
                badgeColor="success"
                items={lists.topUsage || []}
                metric={(company) =>
                  `${formatNumber(company.appointmentsLast30Days)} agendamentos · ${formatNumber(
                    company.commandsLast30Days
                  )} comandas`
                }
                emptyLabel="Nenhum cliente com uso nos últimos 30 dias."
                onOpen={goToCustomers}
              />
              <ListCard
                title="Nunca usaram"
                hint="Cadastraram e não lançaram nada"
                count={counts.neverUsed}
                badgeColor="gray"
                items={lists.neverUsed || []}
                metric={(company) =>
                  `cadastrado há ${formatNumber(company.daysSinceRegistration)} dias`
                }
                emptyLabel="Todos já usaram o sistema."
                onOpen={goToCustomers}
                onSeeAll={goToCustomers}
              />
            </div>
          ) : null}

          {activeTab === "recent" ? (
            <div className="grid grid-cols-1 gap-3">
              <ListCard
                title="Entraram por último"
                hint="Os mais novos da carteira"
                count={lists.recentlyAdded?.length || 0}
                badgeColor="info"
                items={lists.recentlyAdded || []}
                metric={(company) =>
                  `cadastrado em ${formatDate(company.createdAt)}`
                }
                emptyLabel="Nenhum cliente na carteira ainda."
                onOpen={goToCustomers}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default PortfolioDashboard;
